<template>
  <b-row class="justify-content-center m-0">
    <div v-if="loading" class="d-flex justify-content-center p-4">
      <b-spinner label="Loading..." variant="dark" />
    </div>

    <b-col v-else lg="8" class="page__grant-evidence">
      <b-col class="p-0">
        <h5 v-if="learningUnit" class="display-4 font-weight-bold mb-3 header__lu-title">
          {{ learningUnit.name }}
        </h5>
        <div v-if="learningUnit.level_type" class="text-primary font-weight-bold my-4">
          <span class="mr-1"
            ><img
              :src="
                learningUnit.level_type === 'CAREER_SPECIFIC'
                  ? require('@/assets/learning-unit-types/specific.svg')
                  : require('@/assets/learning-unit-types/academic.svg')
              "
              style="height: 16px"
            />
          </span>
          <span class="text__mc-type">{{
            learningUnit.level_type === 'CAREER_SPECIFIC' ? 'Career Specific' : 'Academic Focused'
          }}</span>
        </div>

        <base-back-to-previous-button />

        <div class="mt-u6 font-weight-bold">View submission and feedback</div>
      </b-col>

      <b-col xl="10" class="mt-5 mb-3 p-0 card__submission-evidence">
        <evidence-submission-user-status
          v-model="selectedEvidenceSubmissionId"
          :user="learner"
          :learning-unit="learningUnit"
          class="mb-4 w-100"
        />

        <learning-unit-score-description-card
          v-if="learningUnit.score_description"
          :learning-unit="learningUnit"
          class="mb-4"
        />

        <post-evaluation-assessment-part
          v-for="(partMeta, index) in partMetaData"
          :key="`card__part-${index + 1}-task-list`"
          class="mb-3"
          :part="partMeta"
          :learning-unit="learningUnit"
          :post-ids="partPostIds[partMeta] ? partPostIds[partMeta] : []"
          :editable="false"
          :allow-assess="allowAssess"
          submittable
          assessable
          :disabled="evidenceSubmission.completed || submitting"
          :evidence-submission="evidenceSubmission"
          mode="review"
          :evidence-submitting="onEvidenceSubmitting"
        >
        </post-evaluation-assessment-part>

        <post-evaluation-assessment-submit
          class="mb-4"
          :issuer-id="userId"
          :learning-unit="learningUnit"
          :evidence-submission="evidenceSubmission"
          :allow-assess="allowAssess"
          :readonly="!allowAssess"
          :post-evaluation-assessments="postEvaluationAssessments"
        >
        </post-evaluation-assessment-submit>

        <confirmation-evaluation-assessment
          v-show="allowAssess"
          class="mb-4"
          :issuer-id="userId"
          :learning-unit="learningUnit"
          :evidence-submission="evidenceSubmission"
          :post-evaluation-assessments="postEvaluationAssessments"
          :allow-assess="allowAssess"
          @assessed="fetchEventLog"
          @isEvidenceSubmitting="(v) => (onEvidenceSubmitting = v)"
        ></confirmation-evaluation-assessment>

        <post-evaluation-assessment-history
          v-if="eventLogs.length"
          :items="eventLogs"
          :part-post-ids="partPostIds"
          :evidence-submission-id="selectedEvidenceSubmissionId"
          class="w-100"
        >
        </post-evaluation-assessment-history>
      </b-col>
    </b-col>
  </b-row>
</template>

<script>
import { orderBy } from 'lodash';
import { usePostExtractor } from '@/composables/usePostExtractor';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import { feedMixin } from '@/mixins/feedMixin';
import EventLog from '@/models/EventLog';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import LearningUnit from '@/models/LearningUnit';
import Learner from '@/models/Learner';
import Post from '@/models/Post';
import PostEvaluationAssessmentPart from '@/components/Post/PostEvaluationAssessmentPart';
import PostEvaluationAssessmentSubmit from '@/components/Post/PostEvaluationAssessmentSubmit';
import PostEvaluationAssessmentHistory from '@/components/Post/PostEvaluationAssessmentHistory';
import EvidenceSubmissionUserStatus from '@/components/EvidenceSubmission/EvidenceSubmissionUserStatus';
import BaseBackToPreviousButton from '@/components/base-components/BaseBackToPreviousButton';
import LearningUnitScoreDescriptionCard from '@/components/LearningUnit/LearningUnitScoreDescriptionCard';
import ConfirmationEvaluationAssessment from '@/components/Post/ConfirmationEvaluationAssessment.vue';

export default {
  components: {
    PostEvaluationAssessmentPart,
    EvidenceSubmissionUserStatus,
    BaseBackToPreviousButton,
    PostEvaluationAssessmentSubmit,
    PostEvaluationAssessmentHistory,
    LearningUnitScoreDescriptionCard,
    ConfirmationEvaluationAssessment
  },
  mixins: [feedMixin, userInfoMixin],

  data() {
    return {
      loading: false,
      submitting: false,
      onEvidenceSubmitting: false,
      selectedEvidenceSubmissionId: this.$route.params.evidenceSubmissionId,
      eventLogs: []
    };
  },

  computed: {
    learningUnitId() {
      return Number.parseInt(this.$route.params.learningUnitId);
    },

    learnerId() {
      return Number.parseInt(this.$route.params.learnerId);
    },

    learner() {
      return Learner.find(this.learnerId);
    },

    evidenceSubmissionId() {
      return Number.parseInt(this.selectedEvidenceSubmissionId);
    },

    learningUnit() {
      return LearningUnit.query()
        .with('ultimate_learning_outcome')
        .whereId(this.learningUnitId)
        .first();
    },

    partMetaData() {
      return Object.keys(Post.PART);
    },

    evidenceSubmission() {
      return EvidenceSubmission.find(this.evidenceSubmissionId);
    },

    postEvaluationAssessments() {
      const posts = Post.query()
        .where('post_type', 'EVALUATION_ASSESSMENT')
        .where('learning_unit_id', this.learningUnitId)
        .get();

      return posts;
    },

    partPostIds() {
      const { extractPostIdGroupByPart } = usePostExtractor();

      const { postEvaluationAssessments = [] } = this;

      return postEvaluationAssessments.length
        ? extractPostIdGroupByPart(postEvaluationAssessments)
        : {};
    },

    allowAssess() {
      return (
        this.evidenceSubmission.assessor_reviewed_at !== null &&
        this.evidenceSubmission.issuer_reviewed_at === null
      );
    }
  },

  watch: {
    '$route.path': {
      immediate: true,
      handler: 'onRouteChanged'
    },

    selectedEvidenceSubmissionId: 'fetchEventLog'
  },

  methods: {
    async onRouteChanged() {
      await this.fetch();
    },

    async fetch() {
      this.loading = true;

      await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);

      try {
        await EvidenceSubmission.api().fetchIssuer(this.learningUnitId, {
          user: this.learnerId
        });

        await this.fetchEventLog();
      } catch (error) {
        this.$toast('Cannot load your submissions, please try again');
      } finally {
        setTimeout(() => {
          this.loading = false;
        }, 500);
      }
    },

    async fetchEventLog() {
      const {
        response: { data = [] }
      } = await EventLog.api().fetch({
        learning_unit_id: this.learningUnitId,
        log_group: 'Submission',
        related_id: this.evidenceSubmissionId
      });

      if (data.length) {
        const events = orderBy(data, 'timestamp', 'desc');
        const eventMessages = events.map((event_log) => JSON.parse(event_log.message));

        this.eventLogs = eventMessages
          .filter((message) => message.$metadata.evidenceSubmissionId == this.evidenceSubmissionId)
          .map((message) => ({ message }));
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.page__grant-evidence,
.card__submission-evidence {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
